package com.crowpay.views.components.project.activity.concerns

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.isUnread
import com.crowpay.extensions.readByIntersection
import com.crowpay.extensions.requiresAction
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.views.components.actionIcon
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.dialogs.ChangesRejectedDetails
import com.crowpay.views.dialogs.DrawWaitedDetails
import com.crowpay.views.components.project.work.scope.LineItemCompletionForm
import com.crowpay.views.components.unreadIcon
import com.crowpay.views.dialogs.FileIncidentConfirmation
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.*
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.models.times
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import kotlinx.coroutines.launch

enum class ConcernSection(val displayName: String) {
    All("All"),
    Work("Work"),
    Service("Service"),
    Draws("Draws"),
    Changes("Changes")
}

fun ProjectView.concerns(writer: ViewWriter) = with(writer) {
    val selectedSection = Property(ConcernSection.All)

    val concernCards = shared {
        val cards = ArrayList<ConcernCardInfo>()

        val lineItemMessages = lineItemCompletionMessages().groupBy { it.lineItem }
        val incidentMessages = incidentMessages().groupBy { it.incident }
        val lineComments = lineItemCompletionMessageComments()
        val incidentComments = incidentMessageComments()
        val payApps = payApps().associateBy { it._id }
        val lineItems = lineItems().associateBy { it._id }

        fun List<AdjustedLineItem>.getCards() = mapNotNullTo(cards) { lineItem ->
            val messages = lineItemMessages[lineItem._id] ?: return@mapNotNullTo null
            if (messages.isEmpty()) return@mapNotNullTo null
            ConcernCardInfo(
                ConcernCardInfo.Type.LineItem,
                lineItem._id,
                lineItem.name,
                lineItem.state,
                messages.first().created,
                messages.last(),
                (messages + lineComments.filter { it.lineItem == lineItem._id }).readByIntersection(),
                workItemPermissions()[lineItem._id]?.requiresAction(concernsOnly = true)
            ) {
                LineItemCompletionForm.createDialog(lineItem, project(), lens).apply {
                    if (lineItem.state == LineItemState.Complete) historyExpanded.value = true
                }
            }
        }

        fun List<Incident>.getCards() = mapNotNullTo(cards) { incident ->
            val messages = incidentMessages[incident._id] ?: return@mapNotNullTo null
            ConcernCardInfo(
                ConcernCardInfo.Type.Incident,
                incident._id,
                incident.concern.displayName,
                incident.state,
                incident.filed.timestamp,
                messages.lastOrNull() ?: notFoundError<IncidentMessage>(),
                (messages + incidentComments.filter { it.incident == incident._id }).readByIntersection(),
                incidentPermissions()[incident._id]?.requiresAction(concernsOnly = true)
            ) {
                IncidentReportForm.createDialog(project(), incident, messages, lens)
            }
        }

        fun List<PayAppItem>.getCards() = firstOrNull()?.let { draw ->
            val payApp = payApps[draw.payApp] ?: return@let
            val line = lineItems[draw.lineItem] ?: return@let
            cards.add(ConcernCardInfo(draw, line, payApp, multiple = this@getCards.size > 1) {
                DrawWaitedDetails(
                    payApp,
                    payAppItems.awaitSuspending().filter { it.payApp == payApp._id },
                    lineItems,
                    client.awaitSuspending().name,
                    contractor.awaitSuspending().name
                )
            })
        }

        fun List<ChangeRequestItem>.getCards() = firstOrNull()?.let { change ->
            cards.add(ConcernCardInfo(change, multiple = this@getCards.size > 1) {
                val changeRequest = changeOrders.awaitSuspending().find { it._id == change.changeRequest } ?: notFoundError<ChangeRequest>()
                ChangesRejectedDetails(
                    changeRequest,
                    (pendingItemChanges.awaitSuspending() + pendingLineItems.awaitSuspending()).filter { it.changeRequest == change.changeRequest },
                    client.awaitSuspending().name,
                    contractor.awaitSuspending().name
                )
            })
        }

        when (selectedSection()) {
            ConcernSection.All -> {
                lineItems().filter { it.hasConcernCard(lineItemMessages[it._id] ?: return@filter false) }.getCards()
                incidents().getCards()
                payAppItems().filter { it.denied != null }.getCards()
                pendingItemChanges().filter { it.denied != null }.getCards()
                pendingLineItems().filter { it.denied != null }.getCards()
            }
            ConcernSection.Work -> lineItems().filter { it.hasConcernCard(lineItemMessages[it._id] ?: return@filter false) }.getCards()
            ConcernSection.Service -> incidents().getCards()
            ConcernSection.Draws -> payAppItems().filter { it.denied != null }.getCards()
            ConcernSection.Changes -> {
                pendingItemChanges().filter { it.denied != null }.getCards()
                pendingLineItems().filter { it.denied != null }.getCards()
            }
        }

        cards.sortDescending()

        cards
    }

    col {
        if (lens == ProjectLens.Customer) dismissableBanner("concern tab info") {
            notificationBar(AppColors.blue) - body(
                """
                Concerns about contractor behavior or work performance are listed and accessed here.  Work concerns originate from approval requests and begin by marking the item as Not Ready.  Behavior concerns should be reported as an incident using the “Report Incident” button.  Both types of concerns, if unresolved initially, can escalate to issue and dispute, with opportunities to resolve before the project locks.
                    
                Reporting an Incident makes the contractor aware for documentation, or can be raised to an Issue which formally notices the contractor and requires a contractor response.
                """.trimIndent()
            )
        }

        rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
            compact - fieldTheme - row {
                centered - icon(Icon.filterList.resize(1.rem), "filter")
                (if (viewIsWide) sizeConstraints(width = 10.rem) else expanding) - select {
                    bind(selectedSection, Constant(ConcernSection.entries)) { it.name }
                }
            }
//            for (tab in ConcernSection.entries) {
//                buttonTheme - radioToggleButton {
//                    specCenteredText(tab.displayName)
//                    checked bind selectedSection.equalTo(tab)
//                }
//            }
            if (lens == ProjectLens.Customer) {
                if (viewIsWide) expanding - space()
                ReportIncidentButton.onNext - button {
                    existsDefaultFalse { project().state.canFileIncidents() }
                    specCenteredText("Report Incident")
                    onClick {
                        dialogScreenNavigator.reset(FileIncidentConfirmation(project(), lens))
                    }
                }
            }
        }

        stack {
            sizeConstraints(height = AppDimensions.activityTabMinSize * 0.6) - stack {
                existsDefaultFalse { concernCards().isEmpty() }
                centered - col {
                    colored(AppColors.secondary.light1) - title {
                        content = "Nothing Yet"
                        align = Align.Center
                    }
                    body {
                        content = "When concerns are raised or action is needed they will appear here."
                        align = Align.Center
                    }
                }
            }

            col {
                ::exists { concernCards().isNotEmpty() }
                forEachUpdating(concernCards) { info ->
                    button {
                        spacing = 0.px
                        ::enabled { info().dialog != null }
                        launch {
                            val data = info()
                            if (data.type == ConcernCardInfo.Type.LineItem) JumpTo.LineItemCompletionFlow.newTarget(data.id, this@button)
                        }

                        row {
                            spacing = 0.px
                            section - stack {
                                existsDefaultFalse { info().readBy.isUnread(notNullSession().userId) }
                                centered - unreadIcon()
                            }
                            ConcernCard.onNext - expanding - col {
                                row {
                                    row {
                                        spacing = AppDimensions.expandButtonSpace
                                        centered - actionIcon { info().actionIcon }
                                        centered - subTitle { ::content { info().title } }
                                    }
                                    expanding - centered - smallBody { ::content { info().timestamp.format(Formats.mmddyyyy) } }
                                    centered - bodyBold {
                                        dynamicTheme { DisplayableStateSemantic(info().state) }
                                        ::content { info().state.displayName }
                                    }
                                }
                                compact - col {
                                    row {
                                        bodyBold {
                                            ::content {
                                                if (info().mostRecentMessage.clientMessage) client().name
                                                else contractor().name
                                            }
                                        }
                                        atBottom - smallBody {
                                            ::content { info().mostRecentMessage.created.format(Formats.mmddyyyy_hmm) }
                                        }
                                    }
                                    col {
                                        dynamicTheme {
                                            MessageSemantic(mine = info().mostRecentMessage.sender == notNullSession().userId)
                                        }
                                        emphasize - body { ::content { info().mostRecentMessage.type.displayName } }
                                        body {
                                            ::exists { info().mostRecentMessage.message.isNotBlank() }
                                            ::content { info().mostRecentMessage.message }
                                        }
                                    }
                                }
                            }
                        }

                        OverlayHover.onNext - stack()

                        onClick("View Concern") {
                            val navigator = if (viewIsNarrow) mainScreenNavigator else dialogScreenNavigator
                            info().dialog?.invoke()?.let(navigator::navigate)
                        }
                    }
                }
            }
        }

        space(2.0)
    }
}